/**
 * PWA wiring — service-worker registration + an event surface for the
 * install prompt. Designed to fail open: if anything throws (older browser,
 * no SW support, dev mode), the rest of the app still loads.
 *
 * The install prompt uses the `beforeinstallprompt` event (Chromium / Edge /
 * Android Chrome). Safari iOS users see the standard "Add to Home Screen"
 * via the Share menu — there's no JS hook for that, so we rely on the
 * apple-mobile-web-app-* meta tags in index.html.
 */

let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
const installPromptListeners = new Set<(available: boolean) => void>();

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

export function registerServiceWorker(): void {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  // SW DISABLED (incident remediation). The prior cache-first SW
  // pinned devices to stale, crashing bundles (it fell back to a
  // precached old shell during every deploy's brief 502 window) and
  // a hard-refresh couldn't escape it. Instead of registering, we
  // now actively UNREGISTER any existing SW and purge caches on every
  // load — and never register again. The kill-switch /sw.js handles
  // clients still controlled by the old SW; this handles the rest.
  // No re-registration ⇒ no install→activate→reload loop.
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .getRegistrations()
      .then(regs => regs.forEach(r => r.unregister().catch(() => {})))
      .catch(() => {});
    if (typeof caches !== "undefined") {
      caches
        .keys()
        .then(keys => keys.forEach(k => caches.delete(k).catch(() => {})))
        .catch(() => {});
    }
  });

  window.addEventListener("beforeinstallprompt", (event: Event) => {
    event.preventDefault();
    deferredInstallPrompt = event as BeforeInstallPromptEvent;
    installPromptListeners.forEach(fn => fn(true));
  });

  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt = null;
    installPromptListeners.forEach(fn => fn(false));
  });
}

/** True iff the browser has fired beforeinstallprompt and we haven't consumed it. */
export function isInstallAvailable(): boolean {
  return deferredInstallPrompt !== null;
}

/**
 * Subscribe to install-availability changes. Returns an unsubscribe function.
 * Used by `<InstallPwaButton/>` to render conditionally.
 */
export function onInstallAvailable(fn: (available: boolean) => void): () => void {
  installPromptListeners.add(fn);
  // Fire synchronously with the current state so subscribers don't have to
  // race the load event.
  fn(deferredInstallPrompt !== null);
  return () => {
    installPromptListeners.delete(fn);
  };
}

/**
 * Trigger the platform install prompt. Returns the user's choice. Safe to
 * call when no prompt is pending — the result is `"unavailable"`.
 */
export async function triggerInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  const evt = deferredInstallPrompt;
  if (!evt) return "unavailable";
  await evt.prompt();
  const choice = await evt.userChoice;
  deferredInstallPrompt = null;
  installPromptListeners.forEach(fn => fn(false));
  return choice.outcome;
}
